// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { memo, SyntheticEvent, useMemo } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useRouter } from 'next/router';
import SpeedDial from 'presentations/views/components/atoms/SpeedDial';

import {
  mediaMax,
  fontSize,
  spacing,
  isMediaMax,
  zIndex,
  borderRadius,
  fontWeight,
} from 'presentations/views/styles';
import {
  white,
  gray01,
  primary,
  gray05,
  gray04,
} from 'presentations/views/styles/color';
import dynamic from 'next/dynamic';
const NotificationButton = dynamic(() =>
  import('presentations/views/components/organisms/NotificationButton'),
);
import FloatSettingMenu from 'presentations/views/components/molecules/FloatSettingMenu';
import UserMenuList from 'presentations/views/components/molecules/UserMenuList';
import { SymbolLogo } from 'presentations/views/components/atoms/Logo';
import Icon from 'presentations/views/components/atoms/Icon';
import Link from 'presentations/views/components/atoms/Link';
import Tooltip from 'presentations/views/components/atoms/Tooltip';
import {
  dashboardPath,
  dashboardSearchPath,
  accountSettingPath,
  adminPaymentPath,
  integrationListPath,
  dashboardCalendarsPath,
  pricingPath,
  dashboardStatsPath,
} from 'presentations/routes/path';
import { ViewerItem } from 'presentations/api/schema/user';

import { useDashboardNavigation } from './hooks';
import { PlanId } from 'domains/models/subscription/planId';
import { isAndroid, isiOSApp } from 'presentations/views/helpers/electron';
import CreateMenu from 'presentations/views/components/molecules/CreateMenu';
import { useCreateMenu } from 'presentations/views/components/molecules/CreateMenu/hooks';
import { Role } from 'domains/models/team_user/role';
import Subscription from 'domains/models/subscription';
import { trackClickEvent } from 'presentations/views/helpers/analytics';
import { getDateTime } from 'infras/datetime';
import SearchIndexFeature from 'domains/models/subscription/features/search_index';
import AssistantFeature from 'domains/models/subscription/features/assistant';

interface Props {
  viewer: ViewerItem;
  isShowMobileCreateMenu?: boolean;
  mobileZIndex?: number;
}

export const navigationWidth = 72;

const Component = memo((props: Props) => {
  const t = translateInComponent(translations);
  const { state, actions } = useDashboardNavigation({
    viewer: props.viewer,
  });
  const { state: createState, actions: createActions } = useCreateMenu({
    viewer: props.viewer,
  });
  const isMobile = isMediaMax('tablet');
  const homeUrl = dashboardPath();
  const helpOptions = [
    {
      icon: 'help',
      label: t('Help'),
    },
    {
      icon: 'message-processing-outline',
      label: t('Chat Support'),
    },
  ];
  const router = useRouter();
  const searchPath = dashboardSearchPath({
    from: router.isReady ? router.asPath : dashboardPath(),
  });

  const isAvailableSearch = useMemo(() => {
    const searchIndexFeature = new SearchIndexFeature({
      planVersion: props.viewer.planVersion,
    });
    return searchIndexFeature.isValid(props.viewer.planId);
  }, [props.viewer]);

  const isShowUpgrade = useMemo(() => {
    return (
      props.viewer &&
      props.viewer.planId === PlanId.Free &&
      !isiOSApp() &&
      !isAndroid()
    );
  }, [props.viewer]);

  const isTrial = useMemo(() => {
    return (
      !isiOSApp() &&
      !isAndroid() &&
      props.viewer &&
      Subscription.isDefaultFreeTrial(
        props.viewer.trialEndedAt,
        props.viewer.trialChannel,
      )
    );
  }, [props.viewer]);

  const trialRemainDays = useMemo(() => {
    if (isTrial && props.viewer.trialEndedAt) {
      const days = Math.ceil(
        (getDateTime(props.viewer.trialEndedAt).toMillis() -
          getDateTime().toMillis()) /
          1000 /
          60 /
          60 /
          24,
      );
      if (days > 0) return days;
    }
    return 0;
  }, [props.viewer, isTrial]);

  const payUrl = useMemo(() => {
    const isAdmin = props.viewer.role <= Role.Admin;
    if (isAdmin) {
      return adminPaymentPath();
    }
    return pricingPath();
  }, [props.viewer]);

  const isFullWidth = false;
  if (isMobile && props.isShowMobileCreateMenu) {
    return (
      <>
        <StyledSpeedDial
          open={!!createState.createMenuAnchor}
          onClick={createActions.onClickCreateMenu}
        />
        {props.viewer && (
          <CreateMenu
            anchor={createState.createMenuAnchor}
            onCloseMenu={createActions.onCloseCreateMenu}
            onClickNewEventType={createActions.onClickNewEventType}
            onClickNewForm={createActions.onClickNewForm}
            isShowingPlanUpgrade={createState.isShowingPlanUpgrade}
            onClosePlanUpgrade={createActions.onClosePlanUpgrade}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            viewer={props.viewer}
          />
        )}
      </>
    );
  }

  return (
    <Container mobileZIndex={props.mobileZIndex} isiOSApp={state.isiOSApp}>
      <LeftBox>
        {!state.isElectron && (
          <Link
            href={homeUrl}
            onClick={(e: SyntheticEvent) => {
              if (state.isElectron) {
                e.preventDefault();
                router.replace(homeUrl);
              }
            }}
          >
            <ServiceLogo isDarkable width={32} />
          </Link>
        )}
        {!isMobile && (
          <NavigationContainer>
            {props.viewer && (
              <NavigationList>
                <CreateNavigationListItem>
                  <CreateSpeedDial
                    open={!!createState.createMenuAnchor}
                    onClick={createActions.onClickCreateMenu}
                  />
                </CreateNavigationListItem>
                <NavigationListItem className="tour-target-home">
                  <Tooltip title={t('Home')} placement="right">
                    <NavigationListItemTitle
                      active={state.activePath.dashboard}
                      href={dashboardPath()}
                      onClick={trackClickEvent}
                      dataProps={{
                        ['data-event-name']: 'dashboard header click home',
                      }}
                    >
                      <NavigationIcon name="home" />
                      {isFullWidth && (
                        <NavigationLabel>{t('Home')}</NavigationLabel>
                      )}
                    </NavigationListItemTitle>
                  </Tooltip>
                </NavigationListItem>
                <NavigationListItem className="tour-target-calendar">
                  <Tooltip title={t('Calendar')} placement="right">
                    <NavigationListItemTitle
                      active={state.activePath.calendar}
                      href={dashboardCalendarsPath()}
                      onClick={trackClickEvent}
                      dataProps={{
                        ['data-event-name']: 'dashboard header click calendar',
                      }}
                    >
                      <NavigationIcon name="calendar-month" />
                      {isFullWidth && (
                        <NavigationLabel>{t('Calendar')}</NavigationLabel>
                      )}
                    </NavigationListItemTitle>
                  </Tooltip>
                </NavigationListItem>
                {props.viewer.role < Role.Restricted && (
                  <NavigationListItem>
                    <Tooltip title={t('Analytics')} placement="right">
                      <NavigationListItemTitle
                        active={state.activePath.analytics}
                        href={dashboardStatsPath()}
                        onClick={trackClickEvent}
                        dataProps={{
                          ['data-event-name']:
                            'dashboard header click analytics',
                        }}
                      >
                        <NavigationIcon name="chart-bar" />
                        {isFullWidth && (
                          <NavigationLabel>{t('Analytics')}</NavigationLabel>
                        )}
                      </NavigationListItemTitle>
                    </Tooltip>
                  </NavigationListItem>
                )}
                <NavigationListItem className="tour-target-setting">
                  <Tooltip title={t('Setting')} placement="right">
                    <NavigationListItemTitle
                      active={state.activePath.setting}
                      href={accountSettingPath()}
                      onClick={trackClickEvent}
                      dataProps={{
                        ['data-event-name']: 'dashboard header click settings',
                      }}
                    >
                      <Icon name="settings" size={fontSize(24)} />
                    </NavigationListItemTitle>
                  </Tooltip>
                </NavigationListItem>
                {/* <NavigationListItem className="tour-target-integration">
                  <Tooltip title={t('Integration')} placement="right">
                    <NavigationListItemTitle
                      active={state.activePath.integration}
                      href={integrationListPath()}
                      onClick={trackClickEvent}
                      dataProps={{
                        ['data-event-name']:
                          'dashboard header click integrations',
                      }}
                    >
                      <NavigationIcon name="apps" />
                    </NavigationListItemTitle>
                  </Tooltip>
                </NavigationListItem> */}
              </NavigationList>
            )}
          </NavigationContainer>
        )}
      </LeftBox>
      <RightBox>
        <NavigationList>
          {props.viewer && (
            <>
              {isShowUpgrade ? (
                <>
                  <TrialContent>{t('Free')}</TrialContent>
                  <NavigationListItem>
                    <Tooltip title={t('Upgrade')} placement="right">
                      <NavigationListItemTitle
                        href={payUrl}
                        target="_blank"
                        color="textPrimary"
                        onClick={trackClickEvent}
                        dataProps={{
                          ['data-event-name']: 'dashboard header click upgrade',
                        }}
                      >
                        <Icon name="trophy-variant" size={fontSize(24)} />
                      </NavigationListItemTitle>
                    </Tooltip>
                  </NavigationListItem>
                </>
              ) : (
                isTrial &&
                trialRemainDays && (
                  <>
                    <TrialContent>
                      {t('Remain days', { days: trialRemainDays })}
                    </TrialContent>
                    <NavigationListItem>
                      <Tooltip title={t('Trial')} placement="right">
                        <NavigationListItemTitle
                          href={payUrl}
                          target="_blank"
                          color="textPrimary"
                          onClick={trackClickEvent}
                          dataProps={{
                            ['data-event-name']:
                              'dashboard header click upgrade',
                          }}
                        >
                          <Icon name="trophy-variant" size={fontSize(24)} />
                        </NavigationListItemTitle>
                      </Tooltip>
                    </NavigationListItem>
                  </>
                )
              )}
            </>
          )}
          {AssistantFeature.isAvailableTeamId(props.viewer.team!.id) &&
            props.viewer.role < Role.Restricted && (
              <NavigationListItem>
                <Tooltip title={t('AI')} placement="right">
                  <NavigationListItemTitle
                    active={state.isActiveAssistant}
                    onClick={actions.onClickChat}
                    dataProps={{
                      ['data-event-name']: 'dashboard header click assistant',
                    }}
                  >
                    <NavigationIcon name="creation" />
                  </NavigationListItemTitle>
                </Tooltip>
              </NavigationListItem>
            )}
          {props.viewer.role < Role.Restricted && isAvailableSearch && (
            <NavigationListItem>
              <Tooltip title={t('Search')} placement="right">
                <NavigationListItemTitle
                  active={state.activePath.search}
                  href={searchPath}
                  onClick={trackClickEvent}
                  dataProps={{
                    ['data-event-name']: 'dashboard header click search',
                  }}
                >
                  <NavigationIcon name="search" />
                </NavigationListItemTitle>
              </Tooltip>
            </NavigationListItem>
          )}
          <NavigationListItem>
            <Tooltip title={t('Notification')} placement="right">
              <NavigationListItemTitle
                active={state.activePath.notification}
                onClick={trackClickEvent}
                dataProps={{
                  ['data-event-name']: 'dashboard header click notification',
                }}
              >
                <NotificationButtonContainer>
                  <NotificationButton viewer={props.viewer} />
                </NotificationButtonContainer>
              </NavigationListItemTitle>
            </Tooltip>
          </NavigationListItem>
          <NavigationListItem>
            <NavigationListItemDivider />
          </NavigationListItem>
          <NavigationListItem className="tour-target-help">
            <NavigationListItemTitle
              onClick={actions.onClickHelpMenu}
              color="textPrimary"
            >
              <Icon name="help" size={fontSize(24)} />
            </NavigationListItemTitle>
          </NavigationListItem>
          <NavigationListItem className="tour-target-setting">
            <UserMenuList viewer={props.viewer} isHideExpandIcon />
          </NavigationListItem>
        </NavigationList>
      </RightBox>
      <FloatSettingMenu
        onCloseMenu={actions.onCloseHelpMenu}
        onSelectMenu={actions.onSelectHelpMenu}
        anchor={state.helpAnchor}
        options={helpOptions}
      />
      {props.viewer && (
        <CreateMenu
          anchor={createState.createMenuAnchor}
          onCloseMenu={createActions.onCloseCreateMenu}
          onClickNewEventType={createActions.onClickNewEventType}
          onClickNewForm={createActions.onClickNewForm}
          isShowingPlanUpgrade={createState.isShowingPlanUpgrade}
          onClosePlanUpgrade={createActions.onClosePlanUpgrade}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          viewer={props.viewer}
        />
      )}
    </Container>
  );
});

const Container = styled.div<{
  mobileZIndex?: number;
  isiOSApp: boolean;
}>`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: space-between;
  width: ${navigationWidth}px;
  height: 100vh;
  position: sticky;
  top: 0;
  left: 0;
  background-color: ${white};
  justify-content: space-between;
  -webkit-app-region: drag;
  border-right: 1px solid ${gray05};
  ${mediaMax.tablet} {
    height: 54px;
    /* z-index: ${zIndex(1300)}; */

    ${({ mobileZIndex }) =>
      mobileZIndex &&
      css`
        z-index: ${mobileZIndex};
      `}
  }
  ${({ isiOSApp }) =>
    isiOSApp &&
    css`
      display: none;
    `}
`;
const LeftBox = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-top: ${spacing(20)};
  ${mediaMax.tablet} {
    margin-right: initial;
  }
  ${mediaMax.xs} {
    overflow: hidden;
  }
`;

const NavigationContainer = styled.div``;
const RightBox = styled.div`
  margin-bottom: ${spacing(16)};
  margin-top: ${spacing(16)};
  ${mediaMax.tablet} {
    width: 48px;
  }
`;
const AppNavigation = styled.ul`
  margin-left: ${spacing(64)};
  margin-right: ${spacing(16)};
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    display: none;
  }
`;
const ServiceLogo = styled(SymbolLogo)`
  /* ${mediaMax.tablet} {
    width: 89px;
  } */
`;
export const PaidIcon = styled(Icon)`
  width: 14px !important;
  height: 14px !important;
  margin-right: ${spacing(4)};
`;
export const PaidLink = styled(Link)`
  border-radius: ${borderRadius(6)};
  border: 1px solid ${primary};
  padding: ${spacing(4)} ${spacing(6)};
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(12)};
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    display: none;
  }
`;

const CreateSpeedDial = styled(SpeedDial)`
  width: 36px;
  height: 36px;
`;

const StyledSpeedDial = styled(SpeedDial)`
  position: fixed;
  bottom: 72px;
  right: 12px;
  z-index: ${zIndex(1200)};
  width: 48px;
  height: 48px;
`;

const NavigationList = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
`;
const CreateNavigationListItem = styled.div`
  margin: ${spacing(20)} 0;
  display: flex;
  align-items: center;

  :last-of-type {
    margin-right: 0;
  }
`;
const NavigationListItem = styled.div`
  margin-bottom: ${spacing(20)};
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: center;

  :last-of-type {
    margin-bottom: 0;
  }
`;
const NavigationListItemDivider = styled.div`
  width: 24px;
  border-top: 1px solid ${gray04};
`;
const NavigationListItemTitle = styled(Link)<{ active?: boolean }>`
  font-weight: ${fontWeight('bold')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: ${spacing(2)};
  color: ${({ active }) => (active ? primary : gray01)};
  :hover {
    color: ${primary};
  }
`;

const NavigationIcon = styled(Icon)`
  width: 24px !important;
  height: 24px !important;
  color: inherit;
`;
const TrialContent = styled.div`
  border: 1px solid ${primary};
  color: ${primary};
  margin-bottom: ${spacing(4)};
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(11)};
  padding: ${spacing(2)} ${spacing(4)};
  line-height: 1;
  border-radius: ${borderRadius(4)};
`;
const NotificationButtonContainer = styled.div`
  height: 30px;
`;
const NavigationLabel = styled.div`
  font-size: ${fontSize(10)};
  margin-top: ${spacing(2)};
`;

export default Component;
